import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type Stripe from "stripe";

import useToast from "../../../hooks/useToast";

import { companyBillingQuery } from "../../queries";
import {
	addOrUpdateCreditCard,
	calculateProratedTotal,
	cancelCreditCardSetupIntent,
	createAddSeatsPaymentIntent,
	createCreditCardSetupIntent,
} from "../../services";

export const useCompanyBillingInfo = () => {
	const query = useQuery({
		...companyBillingQuery(),
	});

	return {
		data: query?.data?.[0],
		isLoading: query?.isLoading,
	};
};

export const useCalculateProratedTotal = (setCalculationError: (error: string) => void) => {
	return useMutation({
		mutationFn: async (quantity: string) => await calculateProratedTotal(quantity),
		onError: () => {
			setCalculationError(
				"An error occurred while determining the total purchase price. Please contact our support team for further assistance at support@performyard.com."
			);
		},
	});
};

export const useCreateAddSeatsPaymentIntent = (handleCloseModal: () => void) => {
	const toast = useToast();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (props: { quantity: string; totalAmount: string }) =>
			await createAddSeatsPaymentIntent(props),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["get-billing-info-by-company"] });
			toast("Payment successful", { type: "success" });
			handleCloseModal();
		},
	});
};

export const useCreateCreditCardSetupIntent = (setSetupIntent: (data: Stripe.SetupIntent) => void) => {
	return useMutation({
		mutationFn: async () => await createCreditCardSetupIntent(),
		onSuccess: (data) => setSetupIntent(data),
	});
};

export const useCancelCreditCardSetupIntent = (setSetupIntent: (data: Stripe.SetupIntent) => void) => {
	return useMutation({
		mutationFn: async (setupIntentId: string) => await cancelCreditCardSetupIntent(setupIntentId),
		onSuccess: (data) => setSetupIntent(data),
	});
};

export const useAddOrUpdateCreditCard = (handleCloseModal?: () => void) => {
	const toast = useToast();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (paymentMethodId: string) => await addOrUpdateCreditCard(paymentMethodId),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["get-billing-info-by-company"] });
			toast("Credit card update successful", { type: "success" });
			handleCloseModal?.();
		},
	});
};
